import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { MapPin, Brain, Salad, Moon, BatteryLow, Eye, Flower2, Leaf } from "lucide-react";

interface SeoLink {
  href: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const SEO_LINKS: SeoLink[] = [
  {
    href: "/naturopathe-montesquieu-lauragais",
    title: "Naturopathe à Montesquieu-Lauragais",
    description: "Accompagnement naturel personnalisé au cabinet",
    icon: <MapPin className="w-5 h-5" />,
  },
  {
    href: "/naturopathe-villefranche-de-lauragais",
    title: "Naturopathe à Villefranche-de-Lauragais",
    description: "Consultations à La Maison Céleste, le jeudi matin",
    icon: <MapPin className="w-5 h-5" />,
  },
  {
    href: "/naturopathe-stress",
    title: "Naturopathie & gestion du stress",
    description: "Retrouver calme et équilibre naturellement",
    icon: <Brain className="w-5 h-5" />,
  },
  {
    href: "/naturopathe-digestion",
    title: "Naturopathie & digestion",
    description: "Soutenir le confort digestif au quotidien",
    icon: <Salad className="w-5 h-5" />,
  },
  {
    href: "/naturopathe-sommeil",
    title: "Naturopathie & sommeil",
    description: "Vers des nuits plus sereines, en douceur",
    icon: <Moon className="w-5 h-5" />,
  },
  {
    href: "/naturopathe-fatigue",
    title: "Naturopathie & fatigue",
    description: "Reconnecter avec son énergie vitale",
    icon: <BatteryLow className="w-5 h-5" />,
  },
  {
    href: "/iridologie",
    title: "L'iridologie",
    description: "Comprendre votre terrain à travers l'iris",
    icon: <Eye className="w-5 h-5" />,
  },
  {
    href: "/fleurs-de-bach",
    title: "Les fleurs de Bach",
    description: "Accompagner les émotions naturellement",
    icon: <Flower2 className="w-5 h-5" />,
  },
];

export const SeoLinksHomeSection: React.FC = () => {
  return (
    <section className="py-12 sm:py-16 md:py-20 bg-muted/20" aria-labelledby="seo-links-title">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary/15 mb-4">
              <Leaf className="w-5 h-5 text-secondary" />
            </div>
            <h2
              id="seo-links-title"
              className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary uppercase tracking-wide"
            >
              Domaines d'accompagnement
            </h2>
            <div className="w-20 h-px bg-secondary mx-auto mt-4 mb-4" />
            <p className="text-base sm:text-lg text-foreground/70 font-montserrat max-w-2xl mx-auto">
              Découvrez les différentes approches et problématiques que je vous propose d'explorer ensemble.
            </p>
          </div>

          <nav aria-label="Pages thématiques">
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {SEO_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="group block h-full"
                    aria-label={link.title}
                  >
                    <Card className="h-full p-5 border-primary/20 hover:border-primary/50 hover:shadow-elegant transition-all duration-300 bg-card hover:-translate-y-0.5">
                      <div className="flex flex-col h-full">
                        <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground mb-3 flex-shrink-0">
                          {link.icon}
                        </div>
                        <h3 className="text-base sm:text-lg font-cinzel font-semibold text-primary mb-1.5 leading-snug group-hover:text-secondary transition-colors">
                          {link.title}
                        </h3>
                        <p className="text-sm text-foreground/70 font-montserrat leading-relaxed">
                          {link.description}
                        </p>
                      </div>
                    </Card>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </section>
  );
};
